import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

const SITE_URL = "https://www.shaancurryhouse.com";

// Simple in-memory cache so navigating back and forth doesn't refetch
const seoCache = new Map();

/**
 * Renders <title>, <meta description>, canonical, OG/Twitter tags, and
 * optional BreadcrumbList / BlogPosting JSON-LD for the current route.
 *
 * Values come from GET /api/seo/:route (admin-editable) with a hard-coded
 * fallback so the site never flashes without meta while loading.
 */
export default function SEO({
  route,
  fallbackTitle,
  fallbackDescription,
  breadcrumbs, // [{name, url}] optional
  article, // {headline, image, datePublished, author, description} optional
}) {
  const { pathname } = useLocation();
  const [meta, setMeta] = useState(() => seoCache.get(route) || null);

  useEffect(() => {
    let alive = true;
    if (seoCache.has(route)) {
      setMeta(seoCache.get(route));
      return;
    }
    api
      .get(`/seo/${route}`)
      .then(({ data }) => {
        if (!alive) return;
        seoCache.set(route, data);
        setMeta(data);
      })
      .catch(() => {
        // silent — fallback values below will be used
      });
    return () => {
      alive = false;
    };
  }, [route]);

  const title = meta?.title || fallbackTitle;
  const description = meta?.description || fallbackDescription;
  const keywords = meta?.keywords || "";
  const canonical = meta?.canonical || `${SITE_URL}${pathname}`;
  const ogImage =
    meta?.og_image ||
    "https://www.shaancurryhouse.com/images/site/g13.jpg";

  const breadcrumbLd = breadcrumbs && breadcrumbs.length > 0 && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.url.startsWith("http") ? b.url : `${SITE_URL}${b.url}`,
    })),
  };

  const articleLd = article && {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.headline,
    image: article.image,
    datePublished: article.datePublished,
    author: { "@type": "Organization", name: article.author || "Shaan Curry House" },
    publisher: {
      "@type": "Organization",
      name: "Shaan Curry House",
      logo: {
        "@type": "ImageObject",
        url: "https://www.shaancurryhouse.com/images/site/g13.jpg",
      },
    },
    description: article.description,
    mainEntityOfPage: canonical,
  };

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" href={canonical} hrefLang="en-CA" />
      <link rel="alternate" href={canonical} hrefLang="x-default" />

      {/* Open Graph */}
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:site_name" content="Shaan Curry House" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {breadcrumbLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      )}
      {articleLd && (
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      )}
    </Helmet>
  );
}
